import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { AlertTriangle, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  changePlan,
  clearPlanChange,
  fetchAllPlans,
  initiateCheckout,
  selectAllPlans,
  selectCurrentPlan,
  selectHasLiveSubscription,
  selectPlanChangeError,
  selectPlanChangeLoading,
} from '@/features/subscriptions/subscriptionsSlice'
import { OWNER_ROLE_HINT, selectCanManageBilling } from '@/features/auth/authSlice'
import { upgradeOptionsFor } from '@/features/subscriptions/planOrdering'
import PlanUpgradeOptions from './PlanUpgradeOptions'

// Naive pluralisation renders 'campus' as "Campuss", so spell the labels out.
const RESOURCE_LABELS = {
  user: 'Users',
  campus: 'Campuses',
  dashboard: 'Dashboards',
  metric: 'Metrics',
  data_entry: 'Data entries',
}

const STEP_LIMIT = 'limit'
const STEP_CHOOSE = 'choose'

/** Display label for the resource a limit applies to. */
function getResourceLabel(resource) {
  if (!resource) {
    return 'Resources'
  }
  return RESOURCE_LABELS[resource] || `${resource.charAt(0).toUpperCase()}${resource.slice(1)}s`
}

/**
 * Modal dialog shown when a plan limit is exceeded (403 limit_exceeded from backend).
 * Displays the error detail, current usage vs limit, and an upgrade path.
 *
 * The upgrade happens here, in place: the dialog opens over whatever the user
 * was doing, and closing it puts them back exactly where they were with their
 * work intact. Sending them to Stripe would have lost it - and would have
 * started a second subscription rather than changing the plan
 * (graphit-backend#248). A tenant with no live subscription has nothing to
 * modify, so that case still goes through Stripe Checkout.
 *
 * @param {{ isOpen: boolean, error: object|null, onClose: () => void }} props
 * @returns {JSX.Element}
 */
export default function UpgradePromptDialog({ isOpen, error, onClose }) {
  const dispatch = useDispatch()

  const allPlans = useSelector(selectAllPlans)
  const currentPlan = useSelector(selectCurrentPlan)
  const hasLiveSubscription = useSelector(selectHasLiveSubscription)
  const canManageBilling = useSelector(selectCanManageBilling)
  const changeLoading = useSelector(selectPlanChangeLoading)
  const changeError = useSelector(selectPlanChangeError)

  const [step, setStep] = useState(STEP_LIMIT)
  const [selectedPlanId, setSelectedPlanId] = useState(null)
  const [billingCycle, setBillingCycle] = useState('yearly')

  const upgradeOptions = useMemo(
    () => upgradeOptionsFor(allPlans, currentPlan),
    [allPlans, currentPlan]
  )

  // Reset between openings so a previous attempt's error or selection does not
  // greet the user the next time they hit a limit.
  useEffect(() => {
    if (isOpen) {
      setStep(STEP_LIMIT)
      setSelectedPlanId(null)
      dispatch(clearPlanChange())
    }
  }, [isOpen, dispatch])

  // The plan list is only needed once the user asks to upgrade, and this dialog
  // is mounted app-wide, so it is fetched on demand rather than on every page.
  useEffect(() => {
    if (step === STEP_CHOOSE && allPlans.length === 0) {
      dispatch(fetchAllPlans())
    }
  }, [step, allPlans.length, dispatch])

  // Default to the cheapest plan that actually solves the problem.
  useEffect(() => {
    if (step === STEP_CHOOSE && !selectedPlanId && upgradeOptions.length > 0) {
      setSelectedPlanId(upgradeOptions[0].id)
    }
  }, [step, selectedPlanId, upgradeOptions])

  if (!error) return null

  const { detail, title, meta } = error
  const limit = meta?.limit
  // `current` is what the backend sends; the other two are older shapes kept
  // until every limit response is unified (graphit-backend#201).
  const current = meta?.current ?? meta?.current_count ?? meta?.current_users
  const resource = meta?.resource

  const handleConfirm = async () => {
    if (!selectedPlanId) return

    if (!hasLiveSubscription) {
      // Nothing exists for Stripe to modify, so the first subscription has to
      // go through hosted Checkout - the one case that still navigates away.
      dispatch(initiateCheckout({ planId: selectedPlanId, billingCycle }))
      return
    }

    const result = await dispatch(
      changePlan({ planId: selectedPlanId, billingCycle })
    )

    if (changePlan.fulfilled.match(result)) {
      toast.success(result.payload.message)
      // Straight back to what they were doing, with the new limit in force.
      onClose()
    }
  }

  const isChoosing = step === STEP_CHOOSE
  const hasOptions = upgradeOptions.length > 0

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent size="sm">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning-subtle">
              <AlertTriangle className="h-5 w-5 text-warning-ink" />
            </div>
            <DialogTitle>{title || 'Plan limit reached'}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {isChoosing
              ? hasLiveSubscription
                ? 'Pick a plan. The change applies straight away and you will be returned to what you were doing.'
                : 'Pick a plan. You will be taken to Stripe to enter payment details.'
              : detail || 'You have reached a limit on your current plan.'}
          </DialogDescription>
        </DialogHeader>

        {!isChoosing && limit != null && current != null && (
          <div className="rounded-control border border-line bg-surface-2 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-2">
                {getResourceLabel(resource)} used
              </span>
              <span className="font-semibold text-ink">
                {current} / {limit}
              </span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-line">
              <div
                className="h-full rounded-full bg-warning-solid transition-all"
                style={{ width: `${Math.min((current / limit) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Changing what the organization pays is the owner's call alone. */}
        {!isChoosing && !canManageBilling && (
          <Alert>
            <AlertDescription>{OWNER_ROLE_HINT}</AlertDescription>
          </Alert>
        )}

        {isChoosing && (
          <>
            {hasOptions ? (
              <PlanUpgradeOptions
                plans={upgradeOptions}
                selectedPlanId={selectedPlanId}
                onSelectPlan={setSelectedPlanId}
                billingCycle={billingCycle}
                onChangeBillingCycle={setBillingCycle}
                disabled={changeLoading}
              />
            ) : (
              <Alert>
                <AlertDescription>
                  You are already on the largest plan. Contact support and we will
                  work out what you need.
                </AlertDescription>
              </Alert>
            )}

            {changeError && (
              <Alert variant="danger">
                <AlertDescription>
                  {changeError.message}
                </AlertDescription>
              </Alert>
            )}
          </>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            size="sm"
            onClick={isChoosing ? () => setStep(STEP_LIMIT) : onClose}
            disabled={changeLoading}
          >
            {isChoosing ? 'Back' : 'Dismiss'}
          </Button>

          {canManageBilling && (
            isChoosing ? (
              <Button
                size="sm"
                onClick={handleConfirm}
                disabled={changeLoading || !selectedPlanId}
              >
                {changeLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Changing…
                  </>
                ) : hasLiveSubscription ? (
                  'Confirm upgrade'
                ) : (
                  'Continue to checkout'
                )}
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep(STEP_CHOOSE)}>
                Upgrade plan
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
